/* eslint-disable no-multi-assign */
/* eslint-disable no-restricted-syntax */
import moment from 'moment';
import axios from 'axios';
import _ from 'lodash';
import { CDNPackages } from 'src/globalUtils/CDNPackages';

export const getModuleSize = (size) => {
	if (size <= 576) {
		return 'phone';
	}
	if (size <= 768) {
		return 'tablet';
	}
	if (size <= 992) {
		return 'desktop';
	}
	return 'desktop';
};

/**
 * Attatch script asynchronously to head or other node
 *
 * @category Global Module Utils
 * @module attatchScript
 * @param {string} src source url of package
 * @param {array} attrs attributes added to script tag
 * @param {Node} parentNode - node to attatch script tag to
 */
export const attatchScript = (src, attrs, parentNode) =>
	new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.async = true;
		script.src = src;

		for (const [k, v] of Object.entries(attrs || {})) {
			script.setAttribute(k, v);
		}

		script.onload = () => {
			script.onerror = script.onload = null;
			resolve(script);
		};

		script.onerror = () => {
			script.onerror = script.onload = null;
			reject(new Error(`Failed to load ${src}`));
		};

		const node =
			parentNode || document.head || document.getElementsByTagName('head')[0];
		node.appendChild(script);
	});

/**
 * Used for PDF generation. Creates image element, with dimensions
 *
 * @category Global Module Utils
 * @module getHTMLImageElement
 * @param {string} src
 * @param {number} maxWidth
 * @param {number} maxHeight
 */
export const getHTMLImageElement = (src, maxWidth, maxHeight) =>
	new Promise((resolve, reject) => {
		const img = new Image();

		img.crossOrigin = '';
		img.src = src;

		img.onerror = () => reject();

		img.onload = function () {
			const ratio = Math.min(
				maxWidth / this.naturalWidth,
				maxHeight / this.naturalHeight,
			);

			return resolve({
				img,
				dims: {
					width: this.naturalWidth * ratio,
					height: this.naturalHeight * ratio,
					ratio,
				},
			});
		};
	});

/**
 * @category Global Module Utils
 * @module downloadCSVByTableRows
 * @param {object} rows
 * @param {string} name
 */
export const downloadCSVByTableRows = (rows, name) => {
	const parsedRows = rows.map((row) =>
		_.mapValues(row, (value) => `"${value}"`),
	);

	const csv = [_.keys(parsedRows[0])]
		.concat(parsedRows)
		.map((x) => _.values(x).toString())
		.join('\n');

	const downloadLink = document.createElement('a');
	const blob = new Blob(['\ufeff', csv]);
	const url = URL.createObjectURL(blob);
	const filename =
		name === undefined ? `${name}-${moment().format('DD-MM-YYYY')}` : name;

	downloadLink.href = url;
	downloadLink.download = `${filename}.csv`;
	document.body.appendChild(downloadLink);
	downloadLink.click();
	document.body.removeChild(downloadLink);
};

/**
 * Get the correct skip size based on if one has been specified
 * if not define what the skip amount should be
 *
 * @category Global Module Utils
 * @module getPaginationSkip
 * @param {number} providedSkip a specified skip size
 * @param {number} providedSize a specified size
 * @param {object} pagData pagination data containing totalItemCount and skip
 */
export const getPaginationSkip = (providedSkip, providedSize, pagData) => {
	if (providedSkip !== undefined) {
		return providedSkip;
	}
	if (providedSize > pagData.totalItemCount) {
		return 0;
	}
	return pagData.skip;
};

/**
 * -- DEPRECIATED --
 * Keep fetching from given endpoint until all items are aquired.
 * Adjust the skip each time it fetches.
 *
 * @category Global Module Utils
 * @module fetchAllPaginatedItems
 * @param {string} endPoint
 * @returns {array}
 */
export const fetchAllPaginatedItems = async (endPoint) => {
	let skip = 0;
	let loopCount = 0;
	let items = [];

	const getEndPoint = () =>
		endPoint.replace(/Skip=\d+/, `Skip=${skip}`).replace(/Size=\d+/, `Size=50`);

	const getAllItems = async () => {
		const { data } = await axios.get(getEndPoint());

		skip += 50;
		loopCount += 1;

		if (loopCount > 10) {
			return;
		}

		items = [...items, ...data.items];

		if (data.items.length && items.length < data.totalItemCount) {
			await getAllItems();
		}
	};

	await getAllItems();

	return { items };
};

/**
 * -- DEPRECIATED --
 * Construct a query string and return the url with string attached.
 * Omit any params that are falsy.
 * @category Global Module Utils
 * @module addQueryParams
 * @param {string} url
 * @param {object} queryParams - key / val query params
 */
export const addQueryParams = (url, queryParams) =>
	`${url}${
		queryParams
			? Object.keys(
					_.pickBy(queryParams, (param) => !_.isUndefined(param)),
				).reduce(
					(acc, param, index) =>
						`${acc}${index > 0 ? '&' : ''}${param}=${
							!_.isUndefined(param) ? queryParams[param] : ''
						}`,
					'?',
				)
			: ''
	}`;

/**
 * @category Global Module Utils
 * @module getHasBreakingError
 * @param {Object} errorsObj - The redux state error object
 * @param {array} toValidate - list of errors to use in validation
 */
export const getHasBreakingError = (errorsObj, toValidate = false) => {
	const errors = _.keys(errorsObj).filter((key) => errorsObj[key]);
	if (toValidate) {
		return toValidate.some((key) => errors.includes(key));
	}
	return !!errors.length;
};

export const downloadPDFByTableRows = async (tableRows, name = 'export') => {
	const head = [
		_.keys(tableRows[0]).map((header) => ({
			content: header,
			styles: { fillColor: window.ssUiThemeConfig.colors.primary },
		})),
	];
	const body = tableRows.map((row) =>
		_.keys(row).map((key) => ({ content: row[key] })),
	);

	await CDNPackages.jsPDF();
	const doc = new window.jsPDF();
	doc.setFontSize(10);
	doc.text(`${name}-${moment().format('DD-MM-YYYY')}`, 15, 15);
	doc.autoTable({ html: '#table' });
	doc.autoTable({ head, body });
	window.open(doc.output('bloburl'), '_blank');
};

export const convertCamelCaseToSentenceCase = (string) => {
	const result = string.replace(/([A-Z])/g, ' $1');
	return result.charAt(0).toUpperCase() + result.slice(1);
};

export const getCookieDomainToSet = (hostname) =>
	hostname.includes('schemeserve.com') ? 'schemeserve.com' : hostname;

export const toCamelCase = (str) =>
	str
		.toLowerCase()
		.replace(/(?:^.|[A-Z]|\b.)/g, (letter, index) =>
			index === 0 ? letter.toLowerCase() : letter.toUpperCase(),
		)
		.replace(/\s+/g, '');
